import _ from 'lodash';
import React, { useCallback, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import { useTranslator } from '@jutro/locale';
import { useAuthentication, withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { BreakpointTrackerContext } from '@jutro/layout'
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import TWIADateFormatUtil from "ent-capability-quoteandbind-common-react/util/TWIADateFormatUtil";
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import SuccessPageOfferings from 'tfpa-capability-quoteandbind-residential-react/components/Quote/SuccessPageOfferings/SuccessPageOfferings';
import metadata from './TFPASuccessPage.metadata.json5';
import styles from './TFPASuccessPage.module.scss';
import { Link } from '@jutro/router';

function TFPASuccessfulPage(props) {

    const { location } = props;
    const submissionVM = location?.state?.result || location?.state?.response;
    const auheader = location.state.authHeader;
    const breakpoint = useContext(BreakpointTrackerContext);
    const translator = useTranslator();
    const quoteID = _.get(submissionVM, 'quoteID');
    const policyNumber = _.get(submissionVM, 'bindData_TFPA.policyNumber') || quoteID;
    const documentPath = `${process.env.PUBLIC_PATH}/quotes/${quoteID}/documents`
    const internalPath = `${process.env.PUBLIC_PATH}/quotes/${quoteID}/summary`
    const redirectPath = `${process.env.PUBLIC_PATH}/policies/${policyNumber}/summary`
    const viewModelService = useContext(ViewModelServiceContext);
    const chosenPlan = _.find(submissionVM.bindData_TFPA.paymentPlans, {
        billingId: submissionVM.bindData_TFPA?.selectedPaymentPlan
    });
    const accountHolder = _.get(submissionVM, 'baseData.accountHolder')
    const associationName = _.get(submissionVM, 'lobData.homeowners_tfpa.associationName_TWIA')

    const payerType = viewModelService.productMetadata.get('pc').types.getTypelist('PaymentPayorType_Ext').getCode(submissionVM.bindData_TFPA.payorType_Ext);

    const loanNumberValue = useCallback(() => {
        if (payerType.code === 'mortgageeescrow') {
            const contractNumber = submissionVM.bindData_TFPA.additionalInterests_Ext[0]?.hoadditionalInterestDetails[0].contractNumber;

            if (contactNumber) {
                return contractNumber;
            }
        }

        return null;
    }, []);

    const contactNumber = useCallback(() => {
        const insuredDetails = _.get(submissionVM, 'baseData.accountHolder')
        const contact = formatPhoneNumber(insuredDetails);

        if (contact) {
            return contact;
        }

        return null;
    }, []);


    const formatPhoneNumber = (contact) => {
        const number = getPrimaryPhone(contact);

        if (number === undefined || number === null) {
            return null;
        }

        return number;
    };

    const getPrimaryPhone = (contact) => {
        switch (contact.primaryPhoneType) {
            case 'home':
                return contact.homeNumber;
            case 'work':
                return contact.workNumber;
            case 'mobile':
                return contact.cellNumber;
            default:
                return null;
        }
    }

    const getAddressLineFields = useCallback(() => {
        if (
            submissionVM.lobData.homeowners_tfpa.coverables.yourHome.primaryAddress
        ) {
            return (
                <div>
                    <p>
                        {submissionVM.lobData.homeowners_tfpa.coverables.yourHome.primaryAddress.addressLine1}
                    </p>
                    <p>
                        {submissionVM.lobData.homeowners_tfpa.coverables.yourHome.primaryAddress.city}
                    </p>
                    <p>
                        {submissionVM.lobData.homeowners_tfpa.coverables.yourHome.primaryAddress.state}
                    </p>
                    <p>
                        {submissionVM.lobData.homeowners_tfpa.coverables.yourHome.primaryAddress.postalCode}
                    </p>
                </div>
            );
        }

        return '';
    }, [submissionVM]);

    const quotePrintDocument = useCallback(() => {
        window.print();
    }, []);

    const getReferenceNumber = useCallback(() => {
        return (
            <Link to={internalPath}>
                {quoteID}
            </Link>
        );
    }, []);

    const getPolicyNumber = useCallback(() => {
        return (
            <Link to={redirectPath}>
                {policyNumber}
            </Link>
        );
    }, []);

    const periodEndDate = _.get(submissionVM, `baseData.periodEndDate`);
    const periodStartDate = _.get(submissionVM, `baseData.periodStartDate`);

    const formatPolicyPeriod = (startDate, endDate) => {
        const formattedStartDate = TWIADateFormatUtil.formatDate(startDate);
        const formattedEndDate = TWIADateFormatUtil.formatDate(endDate);

        return `${formattedStartDate} - ${formattedEndDate}`;
    };

    const overrides = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            readOnly: true
        },
        applicationReceived: {
            visible: payerType.code !== 'mortgageeescrow',
        },
        viewSystemRequirement: {
            visible: payerType.code !== 'mortgageeescrow',
        },
        policyIssued: {
            visible: payerType.code === 'mortgageeescrow'
        },
        totalPremium: {
            value: {
                amount: _.get(submissionVM, 'quoteData.offeredQuotes[0].premium.total.amount'),
                currency: "USD"
            }
        },
        requestedCoverageStartDate: {
            label: payerType.code === 'mortgageeescrow' ? 'Policy Effective Date' : 'Proposed Policy Effective Date',
        },
        proposedPolicyPeriod: {
            label: payerType.code === 'mortgageeescrow' ? 'Policy Period' : 'Proposed Policy Period',
            value: formatPolicyPeriod(periodStartDate, periodEndDate)
        },
        addressField: {
            value: getAddressLineFields(),
        },
        paymentPlanName: {
            value: chosenPlan?.name
        },
        name: {
            visible: accountHolder.subtype === 'Company',
            value: accountHolder.contactName
        },
        firstName: {
            visible: accountHolder.subtype === 'Person',
            value: accountHolder.firstName
        },
        lastName: {
            visible: accountHolder.subtype === 'Person',
            value: accountHolder.lastName
        },
        emailAddress: {
            value: accountHolder.emailAddress1
        },
        phoneNumber: {
            visible: contactNumber() !== null && associationName === 'twia', // DE16707
            value: contactNumber()
        },
        policyNumber: {
            visible: payerType.code === 'mortgageeescrow',
            value: getPolicyNumber()
        },
        referenceNumber: {
            visible: payerType.code !== 'mortgageeescrow',
            value: getReferenceNumber()
        },
        loanNumber: {
            visible: payerType.code === 'mortgageeescrow',
            value: loanNumberValue()
        },
        payorPerson: {
            visible: _.get(submissionVM, 'bindData_TFPA.payor_Ext.accountContactDTO.subtype') === 'Person',
            value: _.get(submissionVM, 'bindData_TFPA.payor_Ext.accountContactDTO.displayName')
        },
        payorCompany: {
            visible: _.get(submissionVM, 'bindData_TFPA.payor_Ext.accountContactDTO.subtype') === 'Company',
            value: _.get(submissionVM, 'bindData_TFPA.payor_Ext.accountContactDTO.contactName')
        },
        payorType: {
            value: translator({
                id: `typekey.PaymentPayorType_Ext.${_.get(submissionVM, 'bindData_TFPA.payorType_Ext')}`,
                defaultMessage: `typekey.PaymentPayorType_Ext.${_.get(submissionVM, 'bindData_TFPA.payorType_Ext')}`,
            }),
        },
        offeringDetails: {
            visible: !_.isUndefined(submissionVM),
            readOnly : true,
            submissionVM,
            discSurgValue:_.get(submissionVM,'lobData.homeowners_tfpa.coverages.discountsSurchargesAndFees_Ext'), // DE16719
            insuredValue: _.get(submissionVM,'lobData.homeowners_tfpa.coverages.additionalInsureds_Ext'), // DE16446
            ho205CovDesc: (_.get(submissionVM,'lobData.homeowners_tfpa.coverages.homeOfficeLiabDesc_Ext')),
            ho205CovValue: (_.get(submissionVM,'lobData.homeowners_tfpa.coverages.medPayOthers_Ext')), // DE16447
            isTFPACoverageSummary :true
        },
        fileExtensionLink1: {
            to: documentPath
        },
        fileExtensionLink: {
            to: documentPath
        }

    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            quotePrintDocument,
        },
        resolveComponentMap: {
            offeringComponent: SuccessPageOfferings,
        }
    };

    return (
        <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                // onModelChange={updateWizardData}
                overrideProps={overrides}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
        />
    );
}

TFPASuccessfulPage.propTypes = wizardProps;

export default withRouter(TFPASuccessfulPage);
